/**
 * Country aggregate. Country = tenant boundary; every other aggregate carries
 * a `countryId`. See ADR-0009.
 *
 * This is a pure data shape. The actual per-country *behaviour* (phone
 * validators, ID validators, currency formatters, holiday calendars,
 * regulator contacts) lives in `@dfp/country-config`, which imports this
 * type. Domain code receives validators via the port interfaces in
 * `phone.ts` / `national-id.ts`.
 */
import { z } from "zod";
import { currency } from "../money.js";
const countryCodeRegex = /^[A-Z]{2}$/;
export function countryCode(raw) {
    const upper = raw.toUpperCase();
    if (!countryCodeRegex.test(upper)) {
        throw new TypeError(`Country code must be ISO-3166-1 alpha-2, got "${raw}"`);
    }
    return upper;
}
const languageTagRegex = /^[a-z]{2,3}(-[A-Z]{2})?$/;
export function languageTag(raw) {
    if (!languageTagRegex.test(raw)) {
        throw new TypeError(`Language tag must be BCP-47-ish (sw, en, en-US), got "${raw}"`);
    }
    return raw;
}
/**
 * Boundary validator for Country shapes coming from outside the domain
 * (e.g., loaded from `country_configs`). The domain itself constructs
 * `Country` values directly when it already trusts the source.
 */
export const countrySchema = z.object({
    id: z.string().uuid(),
    code: z.string().length(2).regex(countryCodeRegex),
    name: z.string().min(1),
    currency: z.string().length(3),
    primaryLanguage: z.string().regex(languageTagRegex),
    supportedLanguages: z.array(z.string().regex(languageTagRegex)).min(1),
    timezone: z.string().min(1),
    contractGoverningLanguage: z.string().regex(languageTagRegex),
    config: z.record(z.unknown()).default({}),
});
/** Construct a Country from a boundary-validated record. */
export function makeCountry(raw) {
    const parsed = countrySchema.parse(raw);
    return {
        id: parsed.id,
        code: parsed.code,
        name: parsed.name,
        currency: currency(parsed.currency),
        primaryLanguage: parsed.primaryLanguage,
        supportedLanguages: parsed.supportedLanguages.map((l) => l),
        timezone: parsed.timezone,
        contractGoverningLanguage: parsed.contractGoverningLanguage,
        config: parsed.config,
    };
}
//# sourceMappingURL=country.js.map